import ExcelJS from "exceljs";
import moment from "moment";

export async function getImageFromS3(imagePath: string): Promise<ArrayBuffer | null> {
    return new Promise((resolve) => {
        try {
            let imageUrl: string;
            if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
                imageUrl = imagePath;
            } else {
                imageUrl = `${process.env.REACT_APP_S3}${imagePath}`;
            }
            
            const separator = imageUrl.includes('?') ? '&' : '?';
            const cacheBuster = `_t=${Date.now()}_${Math.random().toString(36).substring(7)}`;
            const imageUrlWithCacheBuster = `${imageUrl}${separator}${cacheBuster}`;
            
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.referrerPolicy = 'origin';
            
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    
                    if (!ctx) {
                        resolve(null);
                        return;
                    }
                    
                    ctx.drawImage(img, 0, 0);
                    
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        
                        blob.arrayBuffer().then((arrayBuffer) => {
                            resolve(arrayBuffer);
                        }).catch(() => {
                            resolve(null);
                        });
                    }, 'image/jpeg');
                } catch (error) {
                    resolve(null);
                }
            };
            
            img.onerror = () => {
                if (imageUrlWithCacheBuster !== imageUrl) {
                    img.src = imageUrl;
                } else {
                    resolve(null);
                }
            };
            
            img.src = imageUrlWithCacheBuster;
        } catch (error) {
            resolve(null);
        }
    });
}

export async function insertImagesToCleaningExcel(
    workbook: ExcelJS.Workbook,
    data: Record<string, Record<string, string[]>>
): Promise<void> {
    for (const worksheet of workbook.worksheets) {
        let rowNumber = 9;

        while (rowNumber <= worksheet.rowCount) {
            const row = worksheet.getRow(rowNumber);
            
            if (!row.hasValues) {
                rowNumber++;
                continue;
            }

            const cellValue = row.getCell(1).value;
            const rowValues = row.values as any[];
            const binCodeValue = cellValue || (Array.isArray(rowValues) ? rowValues[1] : null);
            const binCode = binCodeValue ? String(binCodeValue).trim() : null;

            if (!binCode || !data[binCode]) {
                rowNumber++;
                continue;
            }

            const binData = data[binCode];
            const typeIds = Object.keys(binData);

            const maxImages = Math.max(
                ...typeIds.map(id => binData[id]?.length || 0),
                0
            );

            if (maxImages === 0) {
                rowNumber++;
                continue;
            }

            // Dynamic colMap: starting from column 4, assign one column per image type
            const colMap: Record<string, number> = {};
            typeIds.forEach((typeId, index) => {
                colMap[typeId] = 4 + index;
            });
            const originalRow = worksheet.getRow(rowNumber);

            for (let imageIndex = 0; imageIndex < maxImages; imageIndex++) {
                const targetRowNumber = rowNumber + imageIndex;

                if (imageIndex > 0) {
                    worksheet.spliceRows(targetRowNumber, 0, []);
                }

                const targetRow = worksheet.getRow(targetRowNumber);
                targetRow.height = 260;

                for (const [typeId, col] of Object.entries(colMap)) {
                    const images = binData[typeId] || [];

                    if (images[imageIndex]) {
                        try {
                            const imageBuffer = await getImageFromS3(images[imageIndex]);

                            if (imageBuffer && imageBuffer.byteLength > 0) {
                                let imageExtension = 'jpeg';
                                const imagePath = images[imageIndex].toLowerCase();
                                if (imagePath.includes('.png')) {
                                    imageExtension = 'png';
                                } else if (imagePath.includes('.jpg') || imagePath.includes('.jpeg')) {
                                    imageExtension = 'jpeg';
                                }
                                
                                const imageId = workbook.addImage({
                                    buffer: imageBuffer,
                                    extension: imageExtension as 'jpeg' | 'png',
                                });

                                worksheet.addImage(imageId, {
                                    tl: { col: col - 1 + 0.05, row: targetRowNumber - 1 + 0.05 },
                                    ext: { width: 480, height: 260 },
                                    editAs: 'oneCell'
                                });

                                const imgCell = targetRow.getCell(col);
                                const originalCell = originalRow.getCell(col);
                                const origBorder = originalCell.border;

                                if (origBorder) {
                                    imgCell.border = {
                                        top: origBorder.top || { style: 'thin', color: { argb: 'FF000000' } },
                                        left: origBorder.left || { style: 'thin', color: { argb: 'FF000000' } },
                                        bottom: origBorder.bottom || { style: 'thin', color: { argb: 'FF000000' } },
                                        right: origBorder.right || { style: 'thin', color: { argb: 'FF000000' } },
                                    };
                                } else {
                                    imgCell.border = {
                                        top: { style: 'thin', color: { argb: 'FF000000' } },
                                        left: { style: 'thin', color: { argb: 'FF000000' } },
                                        bottom: { style: 'thin', color: { argb: 'FF000000' } },
                                        right: { style: 'thin', color: { argb: 'FF000000' } },
                                    };
                                }
                            }
                        } catch (error) {
                        }
                    }
                }

                const colsToCopy = [1, 2, 3, ...Object.values(colMap)];
                for (const colNumber of colsToCopy) {
                    const srcCell = originalRow.getCell(colNumber);
                    const dstCell = targetRow.getCell(colNumber);
                    const srcBorder = srcCell.border;

                    if (srcBorder) {
                        dstCell.border = {
                            top: srcBorder.top || { style: 'thin', color: { argb: 'FF000000' } },
                            left: srcBorder.left || { style: 'thin', color: { argb: 'FF000000' } },
                            bottom: srcBorder.bottom || { style: 'thin', color: { argb: 'FF000000' } },
                            right: srcBorder.right || { style: 'thin', color: { argb: 'FF000000' } },
                        };
                    } else {
                        dstCell.border = {
                            top: { style: 'thin', color: { argb: 'FF000000' } },
                            left: { style: 'thin', color: { argb: 'FF000000' } },
                            bottom: { style: 'thin', color: { argb: 'FF000000' } },
                            right: { style: 'thin', color: { argb: 'FF000000' } },
                        };
                    }
                }
            }

            rowNumber += maxImages;
        }
    }
}

export async function insertPeopleCounterDataToExcel(
    workbook: ExcelJS.Workbook,
    data: Record<string, Record<string, number>>,
    selectedMonth?: moment.Moment | null
): Promise<void> {
    workbook.worksheets.forEach((worksheet) => {
        if (selectedMonth) {
            const monthYear = selectedMonth.format('MMM-YYYY').toUpperCase();
            const ah3Cell = worksheet.getCell('AH3');
            ah3Cell.value = monthYear;
            
            const daysInMonth = selectedMonth.daysInMonth();
            if (daysInMonth === 31) {
                const ai6Cell = worksheet.getCell('AI6');
                ai6Cell.value = "31";
            }
        }
        
        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 7 && row.hasValues) {
                const values: any[] = [];
                
                row.eachCell((cell) => {
                    if (cell.value !== null && cell.value !== undefined) {
                        values.push(cell.value);
                    }
                });
                
                if (values.length > 0 && values.every(val => val === values[0])) {
                    return;
                }
                
                const binCodeValue = values[2];
                const binCode = binCodeValue ? String(binCodeValue).trim() : null;
                
                if (binCode && data[binCode]) {
                    const binData = data[binCode];
                    
                    for (const [date, peopleCounter] of Object.entries(binData)) {
                        const dateNum = parseInt(date, 10);
                        const colNumber = dateNum + 4;
                        row.getCell(colNumber).value = peopleCounter;
                    }
                }
            }
        });
    });
}

